import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../config/URL";
import logo from "../../assets/images/Logo-Portal_Access.png";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";
import SendAndPublish from "../Invoice/SendAndPublish";
import { FaFilePdf } from "react-icons/fa";
import { MdOutlineCancel } from "react-icons/md";
import SendAndPublishPaymentReceipt from "./SendAndPublishPaymentReceipt";

const ReceiptView = () => {
  const { id } = useParams();
  const [receiptData, setReceiptData] = useState(null);
  const receptRef = useRef(null);
  const [loadIndicator, setLoadIndicator] = useState(false);
  const navigate = useNavigate();

  const fetchReceiptData = async () => {
    try {
      const response = await api.get(`/getReceiptWithPaymentSpec/${id}`);
      setReceiptData(response.data);
    } catch (error) {
      toast.error("Error fetching receipt data:", error);
    } finally {
      setLoadIndicator(false);
    }
  };
  useEffect(() => {
    fetchReceiptData();
  }, [id]);

  const VoidPayment = async () => {
    try {
      const response = await api.put(`/voidPayment/${receiptData.paymentId}`, {
        headers: { "Content-Type": "application/json" },
      });
      if (response.status === 200) {
        toast.success(response.data.message);
        navigate("/payment");
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.message || "Can't be Cancelled");
    }
  };

  const handleGeneratePDF = async () => {
    const pdf = new jsPDF({
      orientation: "p",
      unit: "px",
      format: "a3",
    });

    const convertImageToDataURL = async (url) => {
      try {
        const response = await fetch(url, { mode: "cors" });
        if (!response.ok) {
          throw new Error(`Failed to fetch image, status: ${response.status}`);
        }
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error(`Error converting image to data URL: ${error.message}`);
        return null; // Return null if the image can't be converted
      }
    };

    const addTableToPDF = async (elementRef) => {
      const element = elementRef.current;

      try {
        element.style.visibility = "visible";
        element.style.display = "block";
        const images = element.querySelectorAll("img");
        for (const img of images) {
          if (img.src && !img.src.startsWith("data:")) {
            const dataURL = await convertImageToDataURL(img.src);
            if (dataURL) {
              img.src = dataURL; // Replace image src with data URL to avoid CORS issues
            }
          }
        }

        const canvas = await html2canvas(element, {
          scale: 2, // Higher scale for better resolution
          useCORS: true, // Allow cross-origin images
          allowTaint: false, // Prevent tainted canvas
        });

        const imgData = canvas.toDataURL("image/png");
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfPageHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;

        // Calculate the scaling factor to fit the content within the PDF width
        const scaleFactor = (pdfWidth - 40) / imgWidth; // 40 for left and right margins (20 each)
        const scaledWidth = imgWidth * scaleFactor;
        const scaledHeight = imgHeight * scaleFactor;

        // Calculate the number of pages needed
        const pageHeight = pdfPageHeight - 40; // 20px margin top and bottom
        const totalPages = Math.ceil(scaledHeight / pageHeight);

        // Add content to PDF page by page
        for (let page = 0; page < totalPages; page++) {
          if (page > 0) {
            pdf.addPage();
          }

          // Calculate the Y position for the current page
          const yOffset = -(page * pageHeight);
          pdf.addImage(
            imgData,
            "PNG",
            20, // Left margin
            20 + yOffset, // Top position adjusted for the page
            scaledWidth,
            scaledHeight,
            undefined,
            "FAST" // Compression for faster rendering
          );
        }

        element.style.visibility = "visible"; // Keep visible as it's the main UI
        element.style.display = "block";
      } catch (error) {
        console.error("Error generating PDF:", error);
        toast.error("Error generating PDF");
      }
    };

    await addTableToPDF(receptRef);

    pdf.save(`${receiptData.receiptNo}.pdf`);
  };

  return (
    <div className="container-fluid mb-2 minHeight">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          Invoice and Payment
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          <Link to="/payment" className="custom-breadcrumb">
            Payment Receipt
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="breadcrumb-item active" aria-current="page">
          Receipt View
        </li>
      </ol>
      <div className="card">
        <div
          className="d-flex px-4 py-2 justify-content-between align-items-center flex-wrap"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center mb-2 mb-md-0">
            <div className="d-flex">
              <div className="dot active"></div>
            </div>
            <span className="ms-2 text-muted">View Invoice</span>
          </div>
          <div className="d-flex align-items-center flex-wrap gap-2">
            {receiptData?.paymentStatus === null && (
              <button
                type="button"
                className="btn btn-border btn-sm"
                onClick={VoidPayment}
              >
                <MdOutlineCancel className="text-danger mb-1 me-1" />
                Void Payment
              </button>
            )}

            {receiptData?.paymentStatus === "PAID" && (
              <SendAndPublishPaymentReceipt
                data={receiptData}
                fetchReceiptData={fetchReceiptData}
                id={id}
              />
            )}
            <button
              onClick={handleGeneratePDF}
              className="btn btn-border btn-sm"
              disabled={loadIndicator}
            >
              {loadIndicator && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  aria-hidden="true"
                ></span>
              )}
              <FaFilePdf className="mb-1 me-1" style={{ color: "#e9841c" }} />
              Generate PDF
            </button>
            <Link to="/payment">
              <button type="button" className="btn btn-sm btn-border">
                Back
              </button>
            </Link>
          </div>
        </div>
        <div className="card shadow border-0 minHeight">
          <div className="container-fluid p-4" ref={receptRef}>
            {/* Header */}
            <div className="d-flex flex-column flex-md-row align-items-start mb-4">
              <img
                src={logo}
                alt="Arty Learning Logo"
                style={{ width: "120px", marginBottom: "10px" }}
              />
              <div className="ms-md-5 mt-2 mt-md-0">
                <h4 style={{ margin: 0 }}>{receiptData?.centerName || "--"}</h4>
                <p style={{ margin: 0 }}>
                  TEL : {receiptData?.centerTelPhoneNumber || "--"}
                </p>
                <p style={{ margin: 0 }}>Email: artylearning@gmail.com</p>
              </div>
            </div>

            <h5 style={{ textDecoration: "underline", fontWeight: "bold" }}>
              Official Receipt
            </h5>

            {/* Receipt Info */}
            <div className="row mt-4 mb-3">
              <div className="col-md-6">
                <div className="receipt-line">
                  <span className="label">Received From</span>
                  <span className="value"> : {receiptData?.receivedFrom}</span>
                </div>
                <div className="receipt-line">
                  <span className="label">Amount</span>
                  <span className="value">
                    : {Number(receiptData?.amount).toLocaleString(
                              "en-IN"
                            )}
                  </span>
                </div>
                <div className="receipt-line">
                  <span className="label">Payment Method</span>
                  <span className="value"> : {receiptData?.paymentMethod}</span>
                </div>
              </div>
              <div className="col-md-6">
                <div className="receipt-line">
                  <span className="label">Receipt No.</span>
                  <span className="value"> : {receiptData?.receiptNo}</span>
                </div>
                <div className="receipt-line">
                  <span className="label">Date</span>
                  <span className="value"> : {receiptData?.date}</span>
                </div>
                <div className="receipt-line">
                  <span className="label">Being Payment Of</span>
                  <span className="value">
                    {" "}
                    : {receiptData?.beingPaymentOf}
                  </span>
                </div>
              </div>
            </div>

            {/* Table Section */}
            <div className="p-2 mb-2" style={{ borderTop: "1px solid black" }}>
              <span className="fw-bold fs-6 my-3">
                Payment Amount Specification
              </span>
              <div className="table-responsive mt-3">
                <table className="table table-responsive invoice-table">
                  <thead>
                    <tr className="bg-dark">
                      <th>Posting Date</th>
                      <th>Document Type</th>
                      <th>Document No.</th>
                      <th>Description</th>
                      <th  className="text-end">Original Amount</th>
                      <th  className="text-end">Allocation Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receiptData?.paymentSpecifications?.length > 0 ? (
                      receiptData.paymentSpecifications.map((spec, index) => (
                        <tr key={spec.id || index}>
                          <td>
                            {new Date(spec.postingDate).toLocaleDateString(
                              "en-GB"
                            )}
                          </td>
                          <td>{spec.documentType}</td>
                          <td>{spec.documentNo}</td>
                          <td>{spec.description}</td>
                          <td className="text-end">
                            {Number(spec.originalAmount).toLocaleString(
                              "en-IN"
                            )}
                          </td>
                          <td className="text-end">
                            {Number(spec.allocationAmount).toLocaleString(
                              "en-IN"
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center text-muted">
                          No records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-4 text-start">
              <p>
                <strong>E. & O. E.</strong>
              </p>
              <p>
                This is a computer generated document and no signature is
                required
              </p>
              {/* <p style={{ fontSize: "14px" }}>
                {receiptData?.centerName || "--"} Tel:{" "}
                {receiptData?.centerTelPhoneNumber || "--"}
              </p> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptView;
